import React, { useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { RateLimitCountdown } from './RateLimitCountdown';
import { useRateLimitCountdown } from '../../hooks/useRateLimitCountdown';
import { formatRateLimitContext, parseBlockedUntil } from '../../utils/rateLimit';
import { cn } from '../../lib/utils';

/**
 * Full-screen, non-dismissible rate-limit overlay shown on the current page.
 * @param {{
 *   blockedUntil: string,
 *   limit?: number | null,
 *   windowSeconds?: number | null,
 *   onExpired?: () => void,
 * }} props
 */
export function RateLimitModal({
  blockedUntil,
  limit = null,
  windowSeconds = 60,
  onExpired,
}) {
  const expiresAt = parseBlockedUntil(blockedUntil);
  const { canRetry } = useRateLimitCountdown({ expiresAt });

  const handleRetry = useCallback(() => {
    if (!canRetry) {
      return;
    }
    window.location.reload();
  }, [canRetry]);

  React.useEffect(() => {
    if (canRetry) {
      onExpired?.();
    }
  }, [canRetry, onExpired]);

  React.useEffect(() => {
    document.body.classList.add('drawer-scroll-lock');

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.body.classList.remove('drawer-scroll-lock');
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  if (!expiresAt) {
    return null;
  }

  const contextMessage = formatRateLimitContext({ limit, windowSeconds });

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => event.preventDefault()}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="rate-limit-title"
        aria-describedby="rate-limit-description"
        className={cn(
          'w-full max-w-md rounded-2xl border p-ds-md',
          'border-border/60 bg-card text-card-foreground shadow-lg',
          'ring-1 ring-border/40'
        )}
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1
              id="rate-limit-title"
              className="text-ds-h2 tracking-tight text-foreground"
            >
              Too many requests
            </h1>
            <p className="text-sm text-muted-foreground">
              Please wait before trying again.
            </p>
          </div>
        </div>

        <p
          id="rate-limit-description"
          className="mb-5 text-sm leading-relaxed text-foreground sm:text-base"
        >
          {contextMessage}
        </p>

        <div className="mb-5">
          <RateLimitCountdown expiresAt={expiresAt} size="large" />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            type="button"
            className="w-full sm:flex-1"
            disabled={!canRetry}
            onClick={handleRetry}
          >
            Try again
          </Button>
          <Button
            asChild
            variant="outline"
            className="w-full sm:flex-1"
            disabled={!canRetry}
          >
            <Link
              to="/help"
              aria-disabled={!canRetry}
              tabIndex={canRetry ? 0 : -1}
              onClick={(event) => {
                if (!canRetry) {
                  event.preventDefault();
                }
              }}
            >
              Get help
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
